import { useState } from 'react';
import type { NotificationRecord } from '@billing/validation';
import {
  Button,
  Container,
  Group,
  Pagination,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { useDocumentTitle } from '@refinedev/react-router-v6';
import { useNavigate } from 'react-router-dom';
import {
  useListNotifications,
  useMarkAllNotificationsRead,
  useSetNotificationRead,
} from 'api/resource/notification';
import { NotificationListItem } from 'components/notifications/NotificationListItem';

const PAGE_SIZE = 20;

export const NotificationCenterPage = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const notifications = useListNotifications({
    page,
    pageSize: PAGE_SIZE,
    unreadOnly,
  });
  const setRead = useSetNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  useDocumentTitle(translate('common:notificationCenter.documentTitle'));

  const openNotification = (notification: NotificationRecord) => {
    if (!notification.readAt) {
      setRead.mutate({ notificationID: notification.id, read: true });
    }
    navigate(notification.actionUrl);
  };

  const totalPages = Math.max(
    1,
    Math.ceil((notifications.data?.total ?? 0) / PAGE_SIZE)
  );

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <Title order={2}>
            {translate('common:notificationCenter.title')}
          </Title>
          <Button
            variant="light"
            disabled={(notifications.data?.unreadCount ?? 0) === 0}
            loading={markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            {translate('common:notificationCenter.markAllRead')}
          </Button>
        </Group>
        <SegmentedControl
          value={unreadOnly ? 'unread' : 'all'}
          data={[
            {
              label: translate('common:notificationCenter.all'),
              value: 'all',
            },
            {
              label: translate('common:notificationCenter.unread'),
              value: 'unread',
            },
          ]}
          onChange={(value) => {
            setUnreadOnly(value === 'unread');
            setPage(0);
          }}
        />
        {notifications.data?.data.length ? (
          notifications.data.data.map((notification) => (
            <NotificationListItem
              key={notification.id}
              notification={notification}
              onOpen={openNotification}
              onToggleRead={(item) =>
                setRead.mutate({
                  notificationID: item.id,
                  read: !item.readAt,
                })
              }
            />
          ))
        ) : (
          <Text ta="center" c="dimmed" py="xl">
            {notifications.isLoading
              ? translate('common:notificationCenter.loading')
              : translate('common:notificationCenter.empty')}
          </Text>
        )}
        {totalPages > 1 && (
          <Pagination
            total={totalPages}
            value={page + 1}
            onChange={(value) => setPage(value - 1)}
            mx="auto"
          />
        )}
      </Stack>
    </Container>
  );
};
